import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HandshakeIcon } from 'lucide-react';
import { toast } from 'sonner';
import { dealsAPI } from '../../api';

const InlineDealDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactName,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    name: '',
    value: 0,
    stage: 'lead',
    expected_close_date: '',
    probability: 20,
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please enter a deal name');
      return;
    }

    try {
      setLoading(true);
      
      await dealsAPI.create({
        name: form.name,
        value: form.value,
        stage: form.stage,
        expected_close_date: form.expected_close_date || null,
        probability: form.probability,
        notes: form.notes,
        contact_id: contactId
      });
      
      toast.success('Deal created successfully');
      setForm({
        name: '',
        value: 0,
        stage: 'lead',
        expected_close_date: '',
        probability: 20,
        notes: ''
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create deal');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      name: '',
      value: 0,
      stage: 'lead',
      expected_close_date: '',
      probability: 20,
      notes: ''
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandshakeIcon className="h-5 w-5 text-green-600" />
            Create Deal
          </DialogTitle>
          <DialogDescription>
            Create a new deal for {contactName}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deal-name">Deal Name *</Label>
            <Input
              id="deal-name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Enterprise License"
              data-testid="inline-deal-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Value ($)</Label>
              <Input
                type="number"
                min="0"
                value={form.value}
                onChange={(e) => setForm(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                data-testid="inline-deal-value"
              />
            </div>
            <div className="space-y-2">
              <Label>Probability (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={form.probability}
                onChange={(e) => setForm(prev => ({ ...prev, probability: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm(prev => ({ ...prev, stage: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Expected Close</Label>
              <Input
                type="date"
                value={form.expected_close_date}
                onChange={(e) => setForm(prev => ({ ...prev, expected_close_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Deal notes..."
              rows={2}
              data-testid="inline-deal-notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.name.trim()}>
              {loading ? 'Creating...' : 'Create Deal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

InlineDealDialog.displayName = 'InlineDealDialog';

export default InlineDealDialog;
